import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { Button } from '@refera/ui-web'

import useRolePermission from '_/hooks/use-role-permission'
import { useMessage } from '_/hooks/use-message'
import { useToast } from '_/hooks/use-toast'

import { userSelector } from '_/modules/authentication/selectors'

import { ROUTES } from '_/utils/constants'
import { PERMISSIONS, PERMISSIONS_ACTIONS } from '_/utils/constants/permissions'
import { DropDownButton } from '_/components/button/drop-down-button'
import { getGenericParametersSelector } from '_/modules/service-orders/selectors'
import { GENERIC_PARAMETERS } from '_/utils/constants/service-order'
import { getGenericParameters } from '_/modules/service-orders/actions'

const NewOrderModal = React.lazy(() =>
  import('../new-order-modal').then(module => ({
    default: module.NewOrderModal,
  }))
)

export function NewServiceOrderButton() {
  const user = useSelector(userSelector)
  const dispatch = useDispatch()
  const genericParameters = useSelector(getGenericParametersSelector)

  const [openModal, setOpenModal] = useState(false)
  const [isMaintenance, setIsMaintenance] = useState()

  const { showMessage } = useMessage()
  const { showToast } = useToast()

  const isNewServiceOrderFormActive = useMemo(
    () => genericParameters?.find(item => item.name === 'new_service_order_form')?.active,
    [genericParameters]
  )

  const { isIntermediaryPerfilParamActive, checkIfUserDoesNotPermission } = useRolePermission()

  const hasMaintenancePermission = useMemo(
    () =>
      !checkIfUserDoesNotPermission(
        PERMISSIONS.INCLUDE_ORDER_MAINTENANCE,
        PERMISSIONS_ACTIONS.ADD,
        undefined,
        false
      ),
    [checkIfUserDoesNotPermission]
  )

  const hasVacancyPermission = useMemo(
    () =>
      !checkIfUserDoesNotPermission(
        PERMISSIONS.INCLUDE_ORDER_VACANCY,
        PERMISSIONS_ACTIONS.ADD,
        undefined,
        false
      ),
    [checkIfUserDoesNotPermission]
  )

  const redirectToSetup = useCallback(
    (isVacancy = false) => {
      if (!user?.agencySlug) {
        showMessage(643)
        return
      }

      let route = isNewServiceOrderFormActive
        ? `${ROUTES.NEW_SERVICE_LOGGED}/${user?.agencySlug}`
        : `${ROUTES.SETUP_AGENCY_FORM_NO_SLUG}/${user?.agencySlug}`

      if (isVacancy) {
        route += '/?vacancy=true'
      }
      window.open(route)
    },
    [user, showMessage, isNewServiceOrderFormActive]
  )
  const handleNewServiceOrder = () => {
    if (!hasMaintenancePermission && !hasVacancyPermission) {
      return showToast({
        type: 'error',
        message: 'Você não tem permissão para esta ação.',
      })
    }

    if (hasMaintenancePermission && !hasVacancyPermission) {
      if (isIntermediaryPerfilParamActive) {
        return redirectToSetup()
      }
      setIsMaintenance(true)
      setOpenModal(true)
    }
    if (!hasMaintenancePermission && hasVacancyPermission) {
      if (!isIntermediaryPerfilParamActive) {
        setIsMaintenance(false)
        setOpenModal(true)
      } else {
        return redirectToSetup(true)
      }
    }
    if (hasMaintenancePermission && hasVacancyPermission) {
      return showToast({
        type: 'error',
        message:
          'Nesta versão, não é possível atender clientes com permissão de incluir chamados de manutenção e desocupação.',
      })
    }
    return null
  }

  const NewServiceButton = () => {
    if (hasMaintenancePermission && hasVacancyPermission) {
      const options = [{ label: 'Manutenção' }, { label: 'Desocupação' }]
      return (
        <DropDownButton label="Novo" options={options} onSelect={() => handleNewServiceOrder()} />
      )
    }

    if (hasMaintenancePermission || hasVacancyPermission) {
      return (
        <Button variant="primary" onClick={handleNewServiceOrder}>
          Novo
        </Button>
      )
    }

    return null
  }

  async function fetchGenericParameter() {
    await dispatch(
      getGenericParameters({
        name: GENERIC_PARAMETERS.NEW_SERVICE_ORDER_FORMS,
        agencyId: user?.agency,
      })
    )
  }

  useEffect(() => {
    if (isNewServiceOrderFormActive === undefined) fetchGenericParameter()
  }, [isNewServiceOrderFormActive])

  return (
    <>
      <NewServiceButton />
      {openModal && (
        <Suspense fallback={null}>
          <NewOrderModal
            title="Escolha da intermediária"
            openModal={openModal}
            setOpenModal={setOpenModal}
            isMaintenance={isMaintenance}
            isNewServiceOrderFormActive={isNewServiceOrderFormActive}
          />
        </Suspense>
      )}
    </>
  )
}
